// Table primitives used by query.tsx.  All values are named exports —
// no window.* plumbing.
//
//   useResizableWidth(storageKey, defaultPx, minPx, maxPx)
//       -> [widthPx, onPointerDown, reset, setWidth]. Used by the filter
//       rail's own drag handle; the results table has fixed column widths
//       (see ColumnDef.width) and doesn't use this.
//   ResultsTableHead({columns, sortKey, sortDir, onSort, ...})
//       Shared <colgroup>+<thead> for any results table: per-column widths,
//       sort glyph, click-to-sort. Column-boundary separators are a plain
//       CSS border (.results-table thead th + th), not JS-rendered.
//   makeComparator(key, dir)
//       Sort comparator over any record by one of its fields, for the
//       results table and the per-store list inside an expanded row.

import React, { useCallback, useState } from "react";

// Shared sort-direction glyphs (↑↓ U+2191/2193).
export const SORT_ASC = "↑";
export const SORT_DESC = "↓";

export type SortDir = "asc" | "desc";

// A null sorts as the lowest value: first ascending, last descending, so
// the THC column sorted high-to-low puts every real reading above the
// rows with none.  Strings compare lowercase with punctuation dropped, so
// "Modern Flower", "mood" and "Moxie" interleave the way a reader
// expects.  Nulls are settled before normalising: comparing -Infinity
// against a string via < or > coerces the string to NaN, every
// comparison comes back false, and the sort loses transitivity on
// string fields.
export function makeComparator<T>(key: string, dir: SortDir): (a: T, b: T) => number {
  const sign = dir === "asc" ? 1 : -1;
  const norm = (v: unknown) =>
    typeof v === "string" ? v.toLowerCase().replace(/[^a-z0-9]/g, "") : v;
  return (a, b) => {
    const aRaw = (a as unknown as Record<string, unknown>)[key];
    const bRaw = (b as unknown as Record<string, unknown>)[key];
    if (aRaw == null && bRaw == null) return 0;
    if (aRaw == null) return -sign;
    if (bRaw == null) return sign;
    const av = norm(aRaw);
    const bv = norm(bRaw);
    if ((av as number) < (bv as number)) return -sign;
    if ((av as number) > (bv as number)) return sign;
    return 0;
  };
}

export interface ColumnDef {
  key: string;
  title?: string;
  sortKey?: string | null;
  isNumeric?: boolean;
  flex?: boolean;
  width?: number;
  headerLeading?: React.ReactNode;
}

export type ResizableTuple = [
  number,
  (e: React.PointerEvent) => void,
  () => void,
  (n: number) => void,
];

function swallowClick(e: Event) {
  e.stopPropagation();
}

// Resizable width with localStorage persistence.  Drag-release synthesises
// a click that would bubble to a sort handler; we swallow it once in
// capture phase so resizing never toggles the sort.
//
// Width changes (and the col-resize body cursor) are deferred until the
// pointer has moved >6px from its press point.  This lets the same handler
// sit on top of a restore button: a quick click reaches the button intact,
// a real drag resizes the column without ever firing the click.
export function useResizableWidth(
  storageKey: string,
  defaultPx: number,
  minPx: number,
  maxPx = 9999,
): ResizableTuple {
  const [w, setW] = useState<number>(() => {
    const v = parseFloat(localStorage.getItem(storageKey) ?? "");
    return !isNaN(v) ? Math.max(minPx, Math.min(maxPx, v)) : defaultPx;
  });
  // Stateless setter that persists.  Exposed as the 4th tuple element for
  // callers (e.g. reset()) that need to drive the width without going
  // through onPointerDown.
  const setWidth = useCallback(
    (nw: number) => {
      setW(nw);
      try {
        localStorage.setItem(storageKey, String(nw));
      } catch {
        /* quota */
      }
    },
    [storageKey],
  );
  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    const startX = e.clientX,
      startW = w;
    let dragged = false;
    const onMove = (ev: PointerEvent) => {
      if (!dragged && Math.abs(ev.clientX - startX) > 6) {
        dragged = true;
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
      }
      if (!dragged) return;
      const nw = Math.round(Math.max(minPx, Math.min(maxPx, startW + ev.clientX - startX)));
      setWidth(nw);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      if (dragged) {
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        window.addEventListener("click", swallowClick, { capture: true, once: true });
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };
  const reset = useCallback(() => setWidth(defaultPx), [setWidth, defaultPx]);
  return [w, onPointerDown, reset, setWidth];
}

export interface ResultsTableHeadProps {
  columns: ColumnDef[];
  sortKey: string | null;
  sortDir: SortDir;
  onSort: (key: string) => void;
  sortInitialDir?: Record<string, SortDir>;
  showInactiveSort?: boolean;
}

// Shared <colgroup>+<thead> for results tables.
//
// `columns` is an array of plain objects, one per visible column in display
// order.  See ColumnDef.  Column widths are fixed (no drag-to-resize);
// boundary separators are a plain CSS border (.results-table thead th + th
// in styles.css), not rendered here.
//
// sortInitialDir lets callers override the first-click direction per sort
// key (the main app sorts thc/cbd/terps/added descending first).  When
// showInactiveSort is true, every sortable column renders a dim glyph in
// its first-click direction; otherwise only the active column shows one.
export function ResultsTableHead({
  columns,
  sortKey,
  sortDir,
  onSort,
  sortInitialDir = {},
  showInactiveSort = false,
}: ResultsTableHeadProps) {
  return (
    <React.Fragment>
      <colgroup>
        {columns.map((col) => (
          <col key={col.key} style={col.flex ? undefined : { width: col.width + "px" }} />
        ))}
      </colgroup>
      <thead>
        <tr>
          {columns.map((col) => {
            const isActive = !!col.sortKey && col.sortKey === sortKey;
            const klass =
              "results-th th-" +
              col.key +
              (col.isNumeric ? " th-num" : "") +
              (col.sortKey ? " is-sortable" : "") +
              (isActive ? " is-sort-active" : "");
            const renderArrow = isActive || (showInactiveSort && col.sortKey);
            const arrowDir: SortDir = isActive
              ? sortDir
              : (sortInitialDir[col.sortKey as string] ?? "asc");
            return (
              <th
                key={col.key}
                data-col={col.key}
                className={klass}
                onClick={col.sortKey ? () => onSort(col.sortKey as string) : undefined}
                title={col.title}
              >
                {col.headerLeading}
                <span className="th-label">
                  {/* Arrow leads the label, matching the mobile sort chip
                      ("↑ LABEL"), so the arrow stays on the same side
                      across viewports. */}
                  {renderArrow && (
                    <span className={"sort-glyph" + (isActive ? " is-active" : "")}>
                      {arrowDir === "asc" ? SORT_ASC : SORT_DESC}
                    </span>
                  )}
                  {col.title}
                </span>
              </th>
            );
          })}
        </tr>
      </thead>
    </React.Fragment>
  );
}
